import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

interface RunwayInputCardProps {
  companyId: string;
}

export default function RunwayInputCard({ companyId }: RunwayInputCardProps) {
  const [cash, setCash] = useState('');
  const [burn, setBurn] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [runway, setRunway] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('company_financials')
        .select('cash_on_hand, monthly_burn, currency, updated_at')
        .eq('company_id', companyId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setCash(String(data.cash_on_hand || ''));
        setBurn(String(data.monthly_burn || ''));
        setCurrency(data.currency || 'USD');
        if (data.cash_on_hand && data.monthly_burn && data.monthly_burn > 0) {
          setRunway(Number(data.cash_on_hand) / Number(data.monthly_burn));
        }
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    if (!cash || !burn) {
      toast.error('Please enter both cash and burn');
      return;
    }

    const cashNum = Number(cash);
    const burnNum = Number(burn);

    if (isNaN(cashNum) || isNaN(burnNum)) {
      toast.error('Please enter valid numbers');
      return;
    }

    if (cashNum < 0 || burnNum < 0) {
      toast.error('Values must be non-negative');
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-financials', {
        body: {
          companyId,
          cash: cashNum,
          burn: burnNum,
          currency,
        },
      });

      if (error) throw error;

      toast.success('Financial data updated');

      if (data?.runway_months !== undefined && data.runway_months !== null) {
        setRunway(data.runway_months);
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (companyId) {
      load();
    }
  }, [companyId]);

  if (loading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="animate-spin h-6 w-6 text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <TrendingUp className="h-5 w-5 text-primary" />
          Financial Snapshot / Runway
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cash">Cash on Hand</Label>
            <Input
              id="cash"
              type="number"
              placeholder="e.g. 2400000"
              value={cash}
              onChange={(e) => setCash(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="burn">Monthly Burn</Label>
            <Input
              id="burn"
              type="number"
              placeholder="e.g. 300000"
              value={burn}
              onChange={(e) => setBurn(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger id="currency">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="ZAR">ZAR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="AED">AED</SelectItem>
                <SelectItem value="KES">KES</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button onClick={save} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>
          {runway !== null && (
            <div className="text-sm text-muted-foreground">
              ≈ <strong className="text-foreground">{runway.toFixed(1)}</strong> months runway
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
